import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export default function UrgencyCard({ variant = "landing" }) {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/config/public").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  // Ronda 32.7 · Root-cause del bug 22: bloqueo real del scroll del body.
  // El backdrop opaco de la ronda anterior era solo cosmético — el problema
  // era «scroll chaining»: al llegar al final del scroll interno del modal,
  // el navegador propagaba el gesto al <body>, moviendo la landing por
  // detrás. Solución: mientras el modal esté abierto, fijamos `overflow:
  // hidden` en <html> y <body> Y preservamos la posición actual con
  // `position: fixed; top: -Ypx` (patrón iOS-safe). Al cerrarse restauramos
  // la posición sin saltos.
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const html = document.documentElement;
    const body = document.body;
    const originalHtmlOverflow = html.style.overflow;
    const originalBodyStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    return () => {
      html.style.overflow = originalHtmlOverflow;
      body.style.overflow = originalBodyStyles.overflow;
      body.style.position = originalBodyStyles.position;
      body.style.top = originalBodyStyles.top;
      body.style.width = originalBodyStyles.width;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  if (!cfg?.urgency_enabled || !cfg?.urgency_message) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="urgency-open-btn"
        className={`w-full text-left block group ${variant === "landing" ? "" : "mt-8"}`}
      >
        <div className="rounded-2xl border-2 border-[#B8912E] bg-white p-6 md:p-8 shadow-[0_8px_30px_rgba(184,145,46,0.14)] transition-all duration-300 group-hover:shadow-[0_16px_44px_rgba(184,145,46,0.22)] group-hover:-translate-y-0.5">
          <div className="flex items-start gap-5">
            <div className="w-12 h-12 rounded-full bg-[#FFF7E5] border border-[#B8912E]/60 flex items-center justify-center shrink-0">
              <Sparkles className="text-[#B8912E]" size={22}/>
            </div>
            <div className="flex-1">
              <div className="label-eyebrow text-[#B8912E]">Alternativa</div>
              <h3 className="font-serif text-2xl md:text-3xl mt-2 text-[#B8912E] font-medium">{cfg.urgency_title}</h3>
              <p className="text-[#0A0A0A] text-base md:text-lg mt-3 leading-relaxed">
                {cfg.urgency_intro || "¿Tienes una consulta urgente o prefieres algo más breve? Esto es lo que Bea recomienda para estos casos."}
              </p>
            </div>
          </div>
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#3B2A17]/85 backdrop-blur-md flex items-center justify-center p-4 overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
            onClick={() => setOpen(false)}
            data-testid="urgency-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="bg-[#FBF7EC] border-2 border-[#B8912E] rounded-2xl max-w-xl w-full p-8 md:p-10 relative shadow-2xl max-h-[85vh] overflow-y-auto overscroll-contain"
              style={{ WebkitOverflowScrolling: "touch" }}
              onClick={(e) => e.stopPropagation()}
              data-testid="urgency-modal-content"
            >
              <button
                onClick={() => setOpen(false)}
                data-testid="urgency-close"
                className="absolute top-4 right-4 text-[#6B6B6B] hover:text-[#0A0A0A] transition-colors"
              >
                <X size={22}/>
              </button>
              <h2 className="font-serif text-2xl md:text-3xl gold font-medium leading-snug">{cfg.urgency_title}</h2>
              <div className="mt-5 text-[#0A0A0A] text-base md:text-lg leading-relaxed whitespace-pre-wrap">
                {cfg.urgency_message}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {(cfg.urgency_phone || "+34984988915") && (
                  <a
                    href={`tel:${(cfg.urgency_phone || "+34984988915").replace(/\s+/g, "")}`}
                    data-testid="urgency-call"
                    className="btn-primary text-sm inline-flex items-center gap-2"
                  >
                    Llamar al gabinete
                  </a>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="btn-ghost text-sm"
                >Volver</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
